import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FunnelIcon, CubeIcon, PhotoIcon } from '@heroicons/react/24/outline';
import StaffShell from '../../StaffShell';
import { inputClass, labelClass } from '../../AuthShell';
import staffApi, { apiError } from '../../api';
import { WORK_LABELS, STATUS_LABELS } from '../../constants';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Skeleton';

export default function OfficeReports() {
  const [technicians, setTechnicians] = useState([]);
  const [filters, setFilters] = useState({ date: '', technician: '', status: '' });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    staffApi.get('/technicians/').then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.date) params.date = filters.date;
    if (filters.technician) params.technician = filters.technician;
    if (filters.status) params.status = filters.status;
    staffApi.get('/reports/', { params })
      .then((r) => setReports(r.data.results || r.data))
      .catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  }, [filters]);

  const set = (e) => setFilters((f) => ({ ...f, [e.target.name]: e.target.value }));
  const clear = () => setFilters({ date: '', technician: '', status: '' });
  const hasFilters = filters.date || filters.technician || filters.status;

  return (
    <StaffShell backTo="/staff/app" max="max-w-3xl">
      <div className="flex items-center gap-2 mb-6">
        <FunnelIcon className="h-6 w-6 text-lime" />
        <h1 className="text-white text-2xl sm:text-3xl font-extrabold">All reports</h1>
      </div>

      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-5 mb-6">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" name="date" value={filters.date} onChange={set} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Technician</label>
            <select name="technician" value={filters.technician} onChange={set} className={inputClass}>
              <option value="">All</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select name="status" value={filters.status} onChange={set} className={inputClass}>
              <option value="">All</option>
              {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        {hasFilters && (
          <button onClick={clear} className="mt-3 text-sm font-semibold text-lime hover:text-green-400">Clear filters</button>
        )}
      </div>

      {error && <p className="text-red-300 text-sm mb-4">{error}</p>}

      {loading ? (
        <SkeletonRows rows={5} />
      ) : reports.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState emoji={hasFilters ? '🔍' : '🗂️'}
            title={hasFilters ? 'No matches' : 'No reports yet'}
            message={hasFilters ? 'No reports match these filters.' : 'Reports will appear here as technicians submit them.'} />
        </div>
      ) : (
        <>
          <p className="text-white/60 text-sm mb-3">{reports.length} report{reports.length === 1 ? '' : 's'}</p>
          <ul className="space-y-3">
            {reports.map((r, i) => (
              <motion.li key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Link to={`/staff/app/report/${r.id}`}
                  className="block rounded-2xl border border-line bg-surface p-4 sm:p-5 hover:border-lime/60 hover:shadow-xl transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-ink font-bold truncate">{r.customer_name || WORK_LABELS[r.work_type] || 'Job'}</div>
                      <div className="text-ink-soft text-sm">
                        {r.date} · {r.technician_name || r.technician_username} · {WORK_LABELS[r.work_type] || r.work_type}
                      </div>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-ink-soft text-xs">
                    <span className="inline-flex items-center gap-1"><CubeIcon className="h-4 w-4" /> {(r.materials_used || []).length}</span>
                    <span className="inline-flex items-center gap-1"><PhotoIcon className="h-4 w-4" /> {(r.photos || []).length}</span>
                    {r.reviewed_by_username && <span className="text-lime font-semibold">Reviewed</span>}
                  </div>
                </Link>
              </motion.li>
            ))}
          </ul>
        </>
      )}
    </StaffShell>
  );
}
